/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  ApiKey,
  ApiScope,
  API_SCOPES,
} from '../domain/entities';
import { RequestStatus, RequestType } from '../modules/rider-ops/types';
import { IModuleRepository } from '../domain/repository';
import { getApiModules, buildApiScope } from './appRegistry';
import { flattenBranches, getBrandByBranchId } from './brandBranches';

export interface SimulatedRequest {
  method: 'GET' | 'POST';
  /** Full path, e.g. '/api/riders' or '/api/riders/abc123/history' */
  endpoint: string;
  apiKey: string;
  body?: Record<string, unknown>;
  /** When set, the system verifies the key's orgId matches this value. */
  orgId?: string;
}

export interface SimulatedResponse {
  status: number;
  statusText: string;
  data: unknown;
  /** Wall-clock ms simulated latency */
  responseTime: number;
}

// ---------------------------------------------------------------------------
// Key validation
// ---------------------------------------------------------------------------

/**
 * Validate an API key and check that it carries the required scope.
 * Supports both legacy scopes (rider:read) AND new registry-derived scopes
 * (rider-manager:read).
 */
export async function validateApiKey(
  keyValue: string,
  requiredScope: ApiScope,
  repository: IModuleRepository,
  orgId?: string
): Promise<ApiKey | null> {
  const keys = await repository.getApiKeys();
  const key = keys.find(k => k.key === keyValue && k.status === 'active');
  if (!key) return null;
  if (!key.scopes.includes(requiredScope)) return null;
  // Tenant isolation: if orgId is provided, the key must belong to that org
  if (orgId && key.orgId && key.orgId !== orgId) return null;
  return key;
}

/**
 * Validate key against a module-derived scope OR a legacy scope.
 * Tries the new registry scope first, then the legacy scope as fallback.
 */
export async function validateApiKeyForModule(
  keyValue: string,
  moduleId: string,
  scopeType: 'read' | 'write',
  repository: IModuleRepository,
  legacyScope?: ApiScope,
  orgId?: string
): Promise<ApiKey | null> {
  const keys = await repository.getApiKeys();
  const key = keys.find(k => k.key === keyValue && k.status === 'active');
  if (!key) return null;
  // Tenant isolation: if orgId is provided, the key must belong to that org
  if (orgId && key.orgId && key.orgId !== orgId) return null;

  const registryScope = buildApiScope(moduleId, scopeType);
  if (key.scopes.includes(registryScope)) return key;
  if (legacyScope && key.scopes.includes(legacyScope)) return key;
  return null;
}

// ---------------------------------------------------------------------------
// Virtual API router
// ---------------------------------------------------------------------------

export async function dispatchApiRequest(
  request: SimulatedRequest,
  repository: IModuleRepository
): Promise<SimulatedResponse> {
  const start = Date.now();

  const respond = (status: number, statusText: string, data: unknown): SimulatedResponse => ({
    status,
    statusText,
    data,
    responseTime: Date.now() - start,
  });

  const { method, endpoint, apiKey } = request;
  const requestOrgId = request.orgId;

  // ── Route: GET /api/riders ──────────────────────────────────────────────
  if (method === 'GET' && endpoint === '/api/riders') {
    const key = await validateApiKeyForModule(apiKey, 'rider-manager', 'read', repository, API_SCOPES.RIDER_READ, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks rider-manager:read scope.' });
    const riders = await repository.getAll();
    return respond(200, 'OK', { total: riders.length, data: riders });
  }

  // ── Route: POST /api/riders ─────────────────────────────────────────────
  if (method === 'POST' && endpoint === '/api/riders') {
    const key = await validateApiKeyForModule(apiKey, 'rider-manager', 'write', repository, API_SCOPES.RIDER_WRITE, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks rider-manager:write scope.' });

    const body = request.body ?? {};
    const brands = (await repository.getBrands()).filter((brand) => brand.enabledModules?.includes('rider-manager'));
    const branches = flattenBranches(brands);
    const branchRef = body.branchId ? String(body.branchId) : body.branchCode ? String(body.branchCode) : body.hubCode ? String(body.hubCode) : '';
    const branch = branches.find(
      (candidate) => candidate.id === branchRef || candidate.code.toLowerCase() === branchRef.toLowerCase(),
    );

    if (!body.name || !body.mobile || !branch) {
      return respond(400, 'Bad Request', { error: 'Required fields: name, mobile, branchId.' });
    }

    const brand = getBrandByBranchId(brands, branch.id);

    const newRequest = {
      id: crypto.randomUUID(),
      type: RequestType.NEW_RIDER,
      status: RequestStatus.PENDING,
      requesterId: 'api',
      requesterName: `API Key: ${key.name}`,
      branchId: branch.id,
      riderData: {
        name: String(body.name),
        mobile: String(body.mobile),
        contract: body.contract ? String(body.contract) : 'Standard',
        branchId: branch.id,
        brandId: brand?.id,
      },
      createdAt: Date.now(),
    };

    await repository.saveRequest(newRequest);
    return respond(201, 'Created', { message: 'Rider request submitted and pending admin approval.', data: newRequest });
  }

  // ── Route: GET /api/riders/:id/history ──────────────────────────────────
  const historyMatch = endpoint.match(/^\/api\/riders\/([^/]+)\/history$/);
  if (method === 'GET' && historyMatch) {
    const key = await validateApiKeyForModule(apiKey, 'rider-manager', 'read', repository, API_SCOPES.RIDER_HISTORY_READ, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks rider-manager:read scope.' });

    const riderId = historyMatch[1];
    const rider = await repository.getById(riderId);
    if (!rider) return respond(404, 'Not Found', { error: `Rider '${riderId}' not found.` });

    const history = rider.history?.length
      ? rider.history
      : [{ branchId: rider.branchId, startDate: rider.createdAt, endDate: null, daysSpent: null }];

    return respond(200, 'OK', {
      riderId: rider.id,
      name: rider.name,
      fileNumber: rider.fileNumber,
      history,
    });
  }

  // ── Route: GET /api/branches ─────────────────────────────────────────────
  if (method === 'GET' && endpoint === '/api/branches') {
    const key = await validateApiKeyForModule(apiKey, 'rider-manager', 'read', repository, API_SCOPES.BRANCHES_READ, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks rider-manager:read scope.' });
    const brands = (await repository.getBrands()).filter((brand) => brand.enabledModules?.includes('rider-manager'));
    const branches = flattenBranches(brands).map((branch) => ({
      branchId: branch.id,
      branchName: branch.name,
      branchCode: branch.code,
      brandId: branch.brandId,
      brandName: getBrandByBranchId(brands, branch.id)?.name ?? 'Unknown brand',
    }));
    return respond(200, 'OK', { total: branches.length, data: branches });
  }

  // ── Route: GET /api/aggregator/campaigns ─────────────────────────────────
  if (method === 'GET' && endpoint === '/api/aggregator/campaigns') {
    const key = await validateApiKeyForModule(apiKey, 'aggregator', 'read', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks aggregator:read scope.' });
    return respond(200, 'OK', { message: 'Campaigns endpoint — simulated.', data: [] });
  }

  // ── Route: POST /api/aggregator/submit ───────────────────────────────────
  if (method === 'POST' && endpoint === '/api/aggregator/submit') {
    const key = await validateApiKeyForModule(apiKey, 'aggregator', 'write', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks aggregator:write scope.' });
    return respond(201, 'Created', { message: 'Campaign items submitted — simulated.', data: request.body ?? {} });
  }

  // ── Route: GET /api/aggregator/submissions ───────────────────────────────
  if (method === 'GET' && endpoint === '/api/aggregator/submissions') {
    const key = await validateApiKeyForModule(apiKey, 'aggregator', 'read', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks aggregator:read scope.' });
    return respond(200, 'OK', { message: 'Submissions endpoint — simulated.', data: [] });
  }

  // ── Route: GET /api/menu/profiles ────────────────────────────────────────
  if (method === 'GET' && endpoint === '/api/menu/profiles') {
    const key = await validateApiKeyForModule(apiKey, 'menu-manage', 'read', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks menu-manage:read scope.' });
    return respond(200, 'OK', { message: 'Menu profiles endpoint — simulated.', data: [] });
  }

  // ── Route: GET /api/menu/items ───────────────────────────────────────────
  if (method === 'GET' && endpoint === '/api/menu/items') {
    const key = await validateApiKeyForModule(apiKey, 'menu-manage', 'read', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks menu-manage:read scope.' });
    return respond(200, 'OK', { message: 'Menu items endpoint — simulated.', data: [] });
  }

  // ── Route: POST /api/menu/profiles ───────────────────────────────────────
  if (method === 'POST' && endpoint === '/api/menu/profiles') {
    const key = await validateApiKeyForModule(apiKey, 'menu-manage', 'write', repository, undefined, requestOrgId);
    if (!key) return respond(401, 'Unauthorized', { error: 'Invalid or missing API key, or key lacks menu-manage:write scope.' });
    return respond(201, 'Created', { message: 'Menu profile created — simulated.', data: request.body ?? {} });
  }

  // ── Fallback ─────────────────────────────────────────────────────────────
  return respond(404, 'Not Found', { error: `No route matches ${method} ${endpoint}` });
}
